import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/api';

/**
 * VerificationWarningBanner component displays a warning to unverified users
 * Shows days remaining until account deletion (7 days from registration)
 */
const VerificationWarningBanner: React.FC = () => {
  const { t } = useTranslation('auth');
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) return;

        // Check if email is confirmed
        if (user.email_confirmed_at) {
          setDaysRemaining(null);
          return;
        }

        // Calculate days remaining (7 days from registration)
        const createdAt = new Date(user.created_at);
        const deadline = new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1000);
        const now = new Date();
        const msRemaining = deadline.getTime() - now.getTime();
        const days = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

        setDaysRemaining(days);
        setUserEmail(user.email || '');
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };

    checkVerificationStatus();

    // Check every hour
    const interval = setInterval(checkVerificationStatus, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (daysRemaining === null || daysRemaining < 0) {
    return null;
  }

  // Determine color based on days remaining
  const bgColor = daysRemaining <= 2
    ? 'bg-red-100 dark:bg-red-900/20 border-red-500'
    : daysRemaining <= 4
    ? 'bg-orange-100 dark:bg-orange-900/20 border-orange-500'
    : 'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-500';

  const textColor = daysRemaining <= 2
    ? 'text-red-800 dark:text-red-200'
    : daysRemaining <= 4
    ? 'text-orange-800 dark:text-orange-200'
    : 'text-yellow-800 dark:text-yellow-200';

  const iconColor = daysRemaining <= 2
    ? 'text-red-600 dark:text-red-400'
    : daysRemaining <= 4
    ? 'text-orange-600 dark:text-orange-400'
    : 'text-yellow-600 dark:text-yellow-400';

  const handleResendVerification = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
      });

      if (error) {
        console.error('Error resending verification:', error);
        alert(t('verification.resendError'));
      } else {
        alert(t('verification.resendSuccess'));
      }
    } catch (error) {
      console.error('Error resending verification:', error);
      alert(t('verification.resendError'));
    }
  };

  return (
    <div className={`border-l-4 p-4 ${bgColor}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${iconColor}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${textColor}`}>
            {t('verification.title')}
          </h3>
          <div className={`mt-2 text-sm ${textColor}`}>
            <p>
              {t('verification.message', { days: daysRemaining })}
            </p>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={handleResendVerification}
              className={`text-sm font-medium ${textColor} underline hover:no-underline focus:outline-none`}
            >
              {t('verification.resendButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationWarningBanner;
